import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  VStack,
  HStack,
  IconButton,
  FormHelperText,
  Grid,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Select,
  ModalFooter,
  Text,
} from "@chakra-ui/react";
import { Field, FieldArray, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { parse, isValid } from "date-fns";
import { AppContext } from "AppProvider/AppContext";

interface Schema {
  fields: Record<string, FieldSchema>;
}

interface FieldSchema {
  type: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  format?: string;
  minDate?: string;
  maxDate?: string;
  pattern?: string;
  required?: boolean;
  properties?: Record<string, FieldSchema>;
  items?: FieldSchema;
  enum?: string[];
  helperText?: any;
  label?: string;
  placeholder?: string;
  isEditable?: Boolean;
  visible?: Boolean;
}

interface DynamicFormProps {
  schema: Schema;
  initialValues: Record<string, any>;
  onSubmit: (
    values: Record<string, any>,
    actions: FormikHelpers<Record<string, any>>
  ) => void;
}

const DynamicFormJson = ({
  formRef,
  schema,
  initialValuess,
  onSubmit,
  originalFields,
  finalFieldValues,
  isDisabled,
}: any) => {
  const { accountDetails, workflowProcess } = useContext(AppContext);
  const [initialValues, setInitialValues] = useState<any>(initialValuess);
  const [addNewField, setAddNewField] = useState({
    open: false,
    data: {
      key: "",
      value: "",
      type: "string",
    },
  });

  useEffect(() => {
    setDynamicSchema(schema);
    setInitialValues(initialValuess);
  }, [schema, initialValuess]);

  const [dynamicSchema, setDynamicSchema] = useState(schema);
  const validationSchema = Yup.object().shape(
    Object.fromEntries(
      Object.entries(dynamicSchema.fields).map(
        ([fieldName, fieldSchema]: any) => [
          fieldName,
          generateYupSchema(fieldSchema),
        ]
      )
    )
  );

  function generateYupSchema(schema: FieldSchema): Yup.Schema<any> {
    switch (schema.type) {
      case "string":
        let yupString = Yup.string();
        if (schema.minLength)
          yupString = yupString.min(
            schema.minLength,
            `Minimum length should be ${schema.minLength} characters`
          );
        if (schema.maxLength)
          yupString = yupString.max(
            schema.maxLength,
            `Maximum length should be ${schema.maxLength} characters`
          );
        if (schema.format === "email")
          yupString = yupString.email("Invalid email format");
        return schema.required
          ? yupString.required("Required field")
          : yupString;
      case "number":
        let yupNumber = Yup.number();
        if (schema.min)
          yupNumber = yupNumber.min(
            schema.min,
            `Minimum value should be ${schema.min}`
          );
        if (schema.max)
          yupNumber = yupNumber.max(
            schema.max,
            `Maximum value should be ${schema.max}`
          );
        return schema.required
          ? yupNumber.required("Required field")
          : yupNumber;
      case "date":
        let yupDate = Yup.date();
        if (schema.format === "dd-mm-yyyy")
          yupDate = yupDate
            .transform(parseDate)
            .typeError("Invalid date format");
        if (schema.minDate)
          yupDate = yupDate.min(
            new Date(schema.minDate),
            `Date should be after ${schema.minDate}`
          );
        if (schema.maxDate)
          yupDate = yupDate.max(
            new Date(schema.maxDate),
            `Date should be before ${schema.maxDate}`
          );
        return schema.required ? yupDate.required("Required field") : yupDate;
      case "boolean":
        let yupBoolean = Yup.boolean().typeError("Invalid boolean value");
        return schema.required
          ? yupBoolean.required("Required field")
          : yupBoolean;
      case "object":
        if (!schema.properties)
          throw new Error("Object field schema must have properties defined.");
        const shape: Record<string, Yup.Schema<any>> = {};
        Object.entries(schema.properties).forEach(([key, value]) => {
          shape[key] = generateYupSchema(value);
        });
        return schema.required
          ? Yup.object().shape(shape).required("Required field")
          : Yup.object().shape(shape);
      case "array":
        if (!schema.items || !schema.items.type)
          throw new Error("Array field schema must have valid items defined.");
        const itemSchema = generateYupSchema(schema.items);
        return schema.required
          ? Yup.array().of(itemSchema).required("Required field")
          : Yup.array().of(itemSchema);
      case "string-enum":
        if (!schema.enum || schema.enum.length === 0)
          throw new Error(
            "String Enum field schema must have enum values defined."
          );
        return schema.required
          ? Yup.string().oneOf(schema.enum).required("Required field")
          : Yup.string().oneOf(schema.enum);
      default:
        return Yup.mixed();
    }
  }

  function parseDate(value: any) {
    const parsedDate = parse(value, "MM/dd/yyyy", new Date());
    return isValid(parsedDate) ? parsedDate : new Date(value);
  }

  function getFieldComponent(
    fieldName: string,
    fieldSchema: FieldSchema,
    errors: any,
    touched: any,
    values: any,
    originalFields: any
  ) {
    const error = getNestedError(errors, fieldName);
    const isTouched = getNestedError(touched, fieldName);
    const showError = !!error;

    // Determine whether to show the label
    const shouldShowLabel = fieldSchema.type !== "object";
    // Determine the display label
    const displayLabel: any =
      fieldSchema?.label?.split("_").join(" ") ||
      fieldName?.split("_")?.join(" ");

    function haseditFieldValue(data: any, path: string): boolean {
      const keys = path.split(".");
      let current = data;

      for (const key of keys) {
        if (current && typeof current === "object") {
          current = current[key];
        } else {
          return false;
        }
      }

      return (
        current?.editFieldValue !== null &&
        current?.editFieldValue !== undefined
      );
    }

    const isEdit = haseditFieldValue(originalFields, fieldName);

    switch (fieldSchema.type) {
      case "string":
        return fieldSchema.visible !== false ? (
          <FormControl id={fieldName} isInvalid={showError} variant="floating">
            <Field
              name={fieldName}
              as={Input}
              placeholder={fieldSchema.placeholder || ""}
              style={{
                borderColor: isEdit ? "red" : undefined,
                borderWidth: "2px",
              }}
              disabled={(workflowProcess?.processName === "domestic" && accountDetails?.level === "level-2" && ['Grand_Total', 'Invoice_Number'].includes(fieldName)) ? true : isDisabled}
              isReadOnly={fieldSchema.isEditable ? false : true}
            />
            <FormLabel>{`${fieldName?.split("_").join(" ")} ${isEdit ? "(edited)" : " "
              }`}</FormLabel>
            {showError && <FormErrorMessage>{error}</FormErrorMessage>}
            {fieldSchema.helperText && (
              <FormHelperText>{fieldSchema.helperText}</FormHelperText>
            )}
          </FormControl>
        ) : null;
      case "number":
        return (
          fieldSchema.visible !== false && (
            <FormControl
              id={fieldName}
              isInvalid={showError}
              variant="floating"
            >
              {/* {shouldShowLabel && renderLabel(displayLabel)} */}
              <Field
                name={fieldName}
                type="number"
                as={Input}
                isReadOnly={fieldSchema.isEditable ? false : true}
                placeholder={fieldSchema.placeholder || ""}
                style={{
                  borderColor: isEdit ? "red" : undefined,
                  borderWidth: "2px",
                }}
                disabled={isDisabled}
              />
              <FormLabel>{`${fieldName?.split("_")?.join(" ")} ${isEdit ? "(edited)" : " "
                }`}</FormLabel>
              {showError && <FormErrorMessage>{error}</FormErrorMessage>}
              {(fieldSchema.helperText || isEdit) && (
                <FormHelperText>{fieldSchema.helperText}</FormHelperText>
              )}
            </FormControl>
          )
        );
      case "boolean":
        return (
          fieldSchema.visible !== false && (
            <FormControl id={fieldName} isInvalid={showError}>
              <Field name={fieldName} type="checkbox">
                {({ field }: any) => (
                  <Checkbox
                    {...field}
                    isChecked={field.value}
                    colorScheme="teal"
                    textTransform={"capitalize"}
                    style={{ borderColor: isEdit ? "red" : undefined }}
                    isReadOnly={fieldSchema.isEditable ? false : true}
                    disabled={isDisabled}
                  >
                    {displayLabel}
                  </Checkbox>
                )}
              </Field>
              {/* <FormLabel>{fieldName}</FormLabel> */}
              {showError && <FormErrorMessage>{error}</FormErrorMessage>}
            </FormControl>
          )
        );
      case "object":
        return (
          fieldSchema.visible !== false && (
            <Box>
              {Object.entries(fieldSchema?.properties || {}).map(
                ([subFieldName, subFieldSchema]) => {
                  const nestedLabel =
                    subFieldSchema.type !== "object"
                      ? subFieldSchema.label || subFieldName
                      : null;
                  return (
                    <Box key={subFieldName} mb={4}>
                      {nestedLabel && (
                        <Heading
                          as="h5"
                          size="sm"
                          mb={5}
                          mt={5}
                          textTransform={"capitalize"}
                        >
                          {nestedLabel}
                        </Heading>
                      )}
                      {getFieldComponent(
                        `${fieldName}.${subFieldName}`,
                        subFieldSchema,
                        errors,
                        touched,
                        values,
                        originalFields
                      )}
                    </Box>
                  );
                }
              )}
            </Box>
          )
        );
      case "date":
        return (
          fieldSchema.visible !== false && (
            <FormControl
              id={fieldName}
              isInvalid={showError}
              variant="floating"
            >
              {/* {shouldShowLabel && renderLabel(displayLabel)} */}
              <Field name={fieldName}>
                {({ field, form }: any) => (
                  <Input
                    type="date"
                    {...field}
                    value={
                      values[fieldName]
                        ? new Date(values[fieldName])
                          .toISOString()
                          .substr(0, 10)
                        : ""
                    }
                    onChange={(event) =>
                      form.setFieldValue(fieldName, event.target.value)
                    }
                    disabled={isDisabled}
                    style={{
                      borderColor: isEdit ? "red" : undefined,
                      borderWidth: "2px",
                    }}
                    isReadOnly={fieldSchema.isEditable ? false : true}
                  />
                )}
              </Field>
              <FormLabel>{`${fieldName?.split("_").join(" ")} ${isEdit ? "(edited)" : " "
                }`}</FormLabel>
              {fieldSchema.helperText && (
                <FormHelperText>{fieldSchema.helperText}</FormHelperText>
              )}
              {showError && <FormErrorMessage>{error}</FormErrorMessage>}
            </FormControl>
          )
        );
      case "array":
        return (
          fieldSchema.visible !== false && (
            <FormControl id={fieldName} isInvalid={showError}>
              {/* {renderLabel(displayLabel)} */}
              <FormLabel>{`${fieldName?.split("_").join(" ")} ${isEdit ? "(edited)" : " "
                }`}</FormLabel>
              <FieldArray name={fieldName}>
                {({ push, remove }) => (
                  <>
                    <Grid templateColumns={"1fr 1fr"} gap={4}>
                      {values[fieldName] &&
                        values[fieldName].map((item: any, index: number) => (
                          <HStack
                            key={index}
                            spacing={2}
                            align="center"
                          // p={2}
                          // shadow={"base"}
                          // rounded={12}
                          >
                            {getFieldComponent(
                              `${fieldName}[${index}]`,
                              fieldSchema.items as FieldSchema,
                              errors,
                              touched,
                              values,
                              originalFields
                            )}
                            <Flex align="center" justify="center">
                              <IconButton
                                aria-label="Remove"
                                icon={<MinusIcon />}
                                onClick={() => remove(index)}
                                colorScheme="red"
                                borderRadius="full"
                                size="xxs"
                              />
                            </Flex>
                          </HStack>
                        ))}
                    </Grid>
                    <Flex justify={"end"} mt={2}>
                      <Button
                        onClick={() => push("")}
                        colorScheme="teal"
                        size={"sm"}
                        leftIcon={<AddIcon />}
                        isDisabled={isDisabled}
                      >
                        Add {displayLabel}
                      </Button>
                    </Flex>
                  </>
                )}
              </FieldArray>
              {showError && <FormErrorMessage>{error}</FormErrorMessage>}
            </FormControl>
          )
        );
      default:
        return (
          fieldSchema.visible !== false && (
            <FormControl
              id={fieldName}
              isInvalid={showError}
              variant="floating"
              mt={2}
            >
              {/* {shouldShowLabel && renderLabel(displayLabel)} */}
              <Field
                name={fieldName}
                as={Input}
                placeholder={fieldSchema.placeholder || ""}
                style={{
                  borderColor: isEdit ? "red" : undefined,
                  borderWidth: "2px",
                }}
                isReadOnly={fieldSchema.isEditable ? false : true}
              />
              <FormLabel>{`${fieldName?.split("_").join(" ")} ${isEdit ? "(edited)" : " "
                }`}</FormLabel>
              {showError && <FormErrorMessage>{error}</FormErrorMessage>}
              {fieldSchema.helperText && (
                <FormHelperText>{fieldSchema.helperText}</FormHelperText>
              )}
            </FormControl>
          )
        );
    }
  }

  function getNestedError(errors: any, fieldName: string): any {
    const fields = fieldName.split(".");
    let error = errors;
    for (const field of fields) {
      if (!error) return undefined;
      error = error[field];
    }
    return error;
  }

  const handleFieldAddition = () => {
    setDynamicSchema((prevSchema: any) => ({
      fields: {
        ...prevSchema.fields,
        [addNewField.data.key]: {
          type: addNewField.data.type,
          label: addNewField.data.key,
          value: addNewField.data.value,
        },
      },
    }));
    setInitialValues(() => ({
      ...initialValues,
      [addNewField.data.key]: addNewField.data.value,
    }));
    setAddNewField({
      open: false,
      data: {
        key: "",
        value: "",
        type: "string",
      },
    });
  };
  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        innerRef={formRef}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit({ values, setSubmitting });
        }}
      >
        {({ isSubmitting, values, errors, touched }) => (
          <Form>
            <VStack
              spacing={3}
              align="stretch"
              // p={5}
              m={4}
            >
              {Object.entries(dynamicSchema.fields).map(
                ([fieldName, fieldSchema]: any) =>
                  fieldSchema.visible !== false && (
                    <Box key={fieldName} mb={3}>
                      {getFieldComponent(
                        fieldName,
                        fieldSchema,
                        errors,
                        touched,
                        values,
                        originalFields
                      )}
                    </Box>
                  )
              )}
              <Flex justifyContent={"end"}>
                <Button
                  onClick={() =>
                    setAddNewField({
                      open: true,
                      data: { type: "string", key: "", value: "" },
                    })
                  }
                  colorScheme="teal"
                  size={"sm"}
                  leftIcon={<AddIcon />}
                  display="none"
                >
                  Add New Fields
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  colorScheme="teal"
                  mt={4}
                  display="none"
                >
                  Submit
                </Button>
              </Flex>
            </VStack>
          </Form>
        )}
      </Formik>
      <Modal
        isOpen={addNewField.open}
        onClose={() => {
          setAddNewField({
            open: false,
            data: {
              key: "",
              value: "",
              type: "string",
            },
          });
        }}
        size={{ base: "xs", lg: "md" }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            fontSize={{ base: "md", lg: "xl" }}
            color="teal.300"
            fontWeight="700"
            p=".6rem .6rem"
          >
            Add New Field
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={2}>
              <FormControl id="key">
                <FormLabel>Field Key</FormLabel>
                <Input
                  value={addNewField.data.key}
                  disabled={isDisabled}
                  onChange={(e) =>
                    setAddNewField((prev: any) => ({
                      ...prev,
                      data: { ...prev.data, key: e.target.value },
                    }))
                  }
                />
              </FormControl>
              <FormControl id="type">
                <FormLabel>Field Type</FormLabel>
                <Select
                  value={addNewField.data.type}
                  onChange={(e: any) => {
                    setAddNewField((prev: any) => ({
                      ...prev,
                      data: { ...prev.data, type: e.target.value },
                    }));
                  }}
                  disabled={isDisabled}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="date">Date</option>
                  {/* <option value="string-enum">String Enum</option> */}
                </Select>
              </FormControl>
              <FormControl id="value">
                <FormLabel>Value</FormLabel>
                {addNewField.data.type === "string" && (
                  <Input
                    value={addNewField.data.value}
                    onChange={(e) =>
                      setAddNewField((prev: any) => ({
                        ...prev,
                        data: { ...prev.data, value: e.target.value },
                      }))
                    }
                  />
                )}
                {addNewField.data.type === "number" && (
                  <Input
                    type="number"
                    value={addNewField.data.value}
                    onChange={(e) =>
                      setAddNewField((prev: any) => ({
                        ...prev,
                        data: { ...prev.data, value: e.target.value },
                      }))
                    }
                  />
                )}
                {addNewField.data.type === "boolean" && (
                  <Select
                    value={addNewField.data.value}
                    onChange={(e: any) =>
                      setAddNewField((prev: any) => ({
                        ...prev,
                        data: { ...prev.data, value: e.target.value },
                      }))
                    }
                    disabled={isDisabled}
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </Select>
                )}
                {addNewField.data.type === "date" && (
                  <Input
                    type="date"
                    value={addNewField.data.value}
                    onChange={(e) =>
                      setAddNewField((prev: any) => ({
                        ...prev,
                        data: { ...prev.data, value: e.target.value },
                      }))
                    }
                    disabled={isDisabled}
                  />
                )}
                {addNewField.data.type === "string-enum" && (
                  <VStack>
                    <Input
                      placeholder="Enum Value"
                      value={addNewField.data.value}
                      onChange={(e) =>
                        setAddNewField((prev: any) => ({
                          ...prev,
                          data: { ...prev.data, value: e.target.value },
                        }))
                      }
                      disabled={isDisabled}
                    />
                  </VStack>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2} display="none">
              <Button colorScheme="green" onClick={handleFieldAddition}>
                Add Field
              </Button>
              <Button
                onClick={() => {
                  setAddNewField({
                    open: false,
                    data: {
                      key: "",
                      value: "",
                      type: "string",
                    },
                  });
                }}
                disabled={isDisabled}
                colorScheme="red"
              >
                Cancel
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DynamicFormJson;